import { useState } from 'react';
import type { ProfileCitation } from './WizardContext';

/**
 * Final-step acknowledgement gate required by ADR-029.
 *
 * Shown after the user clicks "Run Analysis" on the last wizard step. The
 * user must check the confirmation box before the calculate request is
 * actually fired. The timestamp and user ID are recorded on the analysis
 * (see `acknowledgementJson` column).
 */
export interface AcknowledgementModalProps {
  readonly open: boolean;
  readonly submitting: boolean;
  readonly jurisdictionCitation?: ProfileCitation;
  readonly assumptionCitation?: ProfileCitation;
  /**
   * When true, the viewer has edit authority over locked methodology values
   * (owner/consultant). The modal surfaces an override warning and reason box;
   * the backend authoritatively records the actual divergences as
   * `overridesJson` on the analysis.
   */
  readonly canOverride?: boolean;
  readonly overrideReason?: string;
  readonly onOverrideReasonChange?: (reason: string) => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

export function AcknowledgementModal({
  open,
  submitting,
  jurisdictionCitation,
  assumptionCitation,
  canOverride = false,
  overrideReason = '',
  onOverrideReasonChange,
  onCancel,
  onConfirm,
}: AcknowledgementModalProps) {
  const [checked, setChecked] = useState(false);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <dialog
        open
        aria-modal="true"
        aria-labelledby="ack-modal-title"
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
      >
        <h3 id="ack-modal-title" className="text-lg font-semibold text-slate-900">
          Confirm submission
        </h3>

        <div className="mt-4 space-y-3 text-sm text-slate-700">
          <p>
            All project information has been entered by the submitter. Figures should be verified
            for accuracy prior to analysis.
          </p>
          <p>
            The methodology assumptions used in this analysis
            {assumptionCitation ? (
              <>
                {' '}
                were set by{' '}
                <span className="font-medium">
                  {assumptionCitation.lastEditedBy ?? 'an administrator'}
                </span>
                {assumptionCitation.lastEditedAt && (
                  <>
                    {' '}
                    on{' '}
                    <span className="font-medium">
                      {new Date(assumptionCitation.lastEditedAt).toLocaleDateString()}
                    </span>
                  </>
                )}
              </>
            ) : (
              ' were set by an administrator'
            )}{' '}
            and are not modifiable at analysis time.
          </p>
          {jurisdictionCitation && (
            <p className="text-xs text-slate-500">
              Jurisdiction profile: {jurisdictionCitation.name}
              {assumptionCitation && ` · Methodology profile: ${assumptionCitation.name}`}
            </p>
          )}
        </div>

        {canOverride && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <p className="font-semibold">You have override authority.</p>
            <p className="mt-1">
              If any Step 5, 7, or 8 value differs from the active methodology profile, it will be
              captured as an audited <code className="font-mono">override</code> on this analysis
              and disclosed on the final report. Please provide a brief reason so reviewers
              understand the departure.
            </p>
            <label htmlFor="override-reason" className="mt-2 block font-medium">
              Reason for override (optional but recommended)
            </label>
            <textarea
              id="override-reason"
              rows={2}
              value={overrideReason}
              onChange={(e) => onOverrideReasonChange?.(e.target.value)}
              placeholder="e.g. Applied client-specific multiplier per engagement scope."
              className="mt-1 block w-full rounded border border-amber-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
        )}

        <label className="mt-5 flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-1"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span>
            I acknowledge that the project data entered above has been verified for accuracy and
            that I am authorized to submit this analysis on behalf of my organization.
          </span>
        </label>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!checked || submitting}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {submitting ? 'Running…' : 'Run Analysis'}
          </button>
        </div>
      </dialog>
    </div>
  );
}
